import type { CreatureState, Input } from '@battle/sim';
import { NEUTRAL_INPUT } from '@battle/sim';
import type { TickedInput } from './protocol.ts';

/**
 * Server side, one per player: inputs arrive early (the client runs ahead) and redundantly.
 * `take(tick)` returns that tick's input, or repeats the last one if it's missing (docs/05).
 */
export class ServerInputBuffer {
  private inputs = new Map<number, Input>();
  private last: Input = NEUTRAL_INPUT;
  lastApplied = 0;
  latestReceived = 0;
  misses = 0;

  push(batch: TickedInput[]) {
    for (const i of batch) {
      if (i.tick <= this.lastApplied || this.inputs.has(i.tick)) continue;
      // Ignore absurdly far-future ticks (clock bugs or abuse).
      if (this.lastApplied && i.tick > this.lastApplied + 600) continue;
      this.inputs.set(i.tick, i);
      if (i.tick > this.latestReceived) this.latestReceived = i.tick;
    }
  }

  take(tick: number): Input {
    const input = this.inputs.get(tick);
    if (input) {
      this.inputs.delete(tick);
      this.last = input;
    } else {
      this.misses++;
    }
    this.lastApplied = tick;
    // Drop anything that can never be applied now.
    for (const t of this.inputs.keys()) if (t <= tick) this.inputs.delete(t);
    return this.last;
  }

  /** Ticks the newest input is ahead of the server tick (negative = the client is late). */
  lead(serverTick: number) {
    return this.latestReceived - serverTick;
  }
}

/** Client side: inputs sent but not yet acknowledged, for replay during reconciliation. */
export class PendingInputs {
  private list: TickedInput[] = [];

  add(i: TickedInput) {
    this.list.push(i);
    if (this.list.length > 240) this.list.shift();
  }
  ack(tick: number) {
    while (this.list.length && (this.list[0] as TickedInput).tick <= tick) this.list.shift();
  }
  get all(): readonly TickedInput[] {
    return this.list;
  }
  /** The last N inputs, for redundant sending. */
  recent(n: number): TickedInput[] {
    return this.list.slice(-n);
  }
}

interface Frame {
  tick: number;
  creatures: Map<number, CreatureState>;
}

/**
 * Client side: remote creatures render `delay` ticks in the past, interpolated between the two
 * buffered snapshots around the render tick; up to `maxExtrapolate` ticks past the newest.
 */
export class InterpolationBuffer {
  private frames: Frame[] = [];

  maxExtrapolate: number;

  constructor(maxExtrapolate = 6) {
    this.maxExtrapolate = maxExtrapolate;
  }

  push(tick: number, creatures: CreatureState[]) {
    if (this.frames.length && tick <= (this.frames[this.frames.length - 1] as Frame).tick) return;
    this.frames.push({ tick, creatures: new Map(creatures.map((c) => [c.handle, c])) });
    if (this.frames.length > 60) this.frames.shift();
  }

  get newest() {
    return this.frames[this.frames.length - 1]?.tick ?? 0;
  }

  get depth() {
    return this.frames.length;
  }

  /** Interpolated state of `handle` at a fractional server tick. */
  sample(handle: number, tick: number): CreatureState | null {
    const f = this.frames;
    if (!f.length) return null;
    let a: Frame | undefined;
    let b: Frame | undefined;
    for (let i = f.length - 1; i >= 0; i--) {
      const fr = f[i] as Frame;
      if (fr.tick <= tick) {
        a = fr;
        b = f[i + 1];
        break;
      }
    }
    if (!a) return (f[0] as Frame).creatures.get(handle) ?? null;
    const sa = a.creatures.get(handle);
    if (!sa) return null;
    if (!b) {
      // Past the newest snapshot: extrapolate briefly along velocity, then hold.
      const dt = Math.min(tick - a.tick, this.maxExtrapolate) / 60;
      return {
        ...sa,
        pos: {
          x: sa.pos.x + sa.vel.x * dt,
          y: sa.pos.y + (sa.grounded ? 0 : sa.vel.y * dt),
          z: sa.pos.z + sa.vel.z * dt,
        },
      };
    }
    const sb = b.creatures.get(handle) ?? sa;
    const k = (tick - a.tick) / (b.tick - a.tick);
    const lerp = (x: number, y: number) => x + (y - x) * k;
    const dy = Math.atan2(Math.sin(sb.yaw - sa.yaw), Math.cos(sb.yaw - sa.yaw));
    return {
      ...(k < 0.5 ? sa : sb),
      pos: {
        x: lerp(sa.pos.x, sb.pos.x),
        y: lerp(sa.pos.y, sb.pos.y),
        z: lerp(sa.pos.z, sb.pos.z),
      },
      vel: {
        x: lerp(sa.vel.x, sb.vel.x),
        y: lerp(sa.vel.y, sb.vel.y),
        z: lerp(sa.vel.z, sb.vel.z),
      },
      yaw: sa.yaw + dy * k,
    };
  }
}

/**
 * Client clock: estimates the server tick from ping samples (median RTT) and steers how far
 * ahead the client simulates, using the server's measured input lead as feedback (docs/05
 * "Clock sync" and time dilation).
 */
export class ClientClock {
  private rtts: number[] = [];
  rttMs = 0;
  jitterMs = 0;

  onPong(sentAt: number, now: number) {
    this.rtts.push(now - sentAt);
    if (this.rtts.length > 15) this.rtts.shift();
    const sorted = [...this.rtts].sort((a, b) => a - b);
    this.rttMs = sorted[Math.floor(sorted.length / 2)] ?? 0;
    const lo = sorted[Math.floor(sorted.length * 0.1)] ?? 0;
    const hi = sorted[Math.floor(sorted.length * 0.9)] ?? 0;
    this.jitterMs = hi - lo;
  }

  /** Extra lead added after inputs arrived late (adaptive input buffer); decays when calm. */
  private extraLead = 0;
  private calmSince = 0;

  /** Feed each snapshot's reported lead: late inputs grow the buffer, calm periods shrink it. */
  noteLead(reportedLead: number, nowMs: number) {
    if (reportedLead < 1) {
      this.extraLead = Math.min(12, this.extraLead + 2);
      this.calmSince = nowMs;
    } else if (nowMs - this.calmSince > 3000 && this.extraLead > 0) {
      this.extraLead--;
      this.calmSince = nowMs;
    }
  }

  /** Target input lead in ticks: 2 ticks of cushion, measured jitter, and the adaptive extra. */
  targetLead() {
    return 2 + Math.ceil(this.jitterMs / (1000 / 60)) + this.extraLead;
  }

  /**
   * Sim speed multiplier from the server-reported lead: run faster when inputs arrive late,
   * slower when too early (±5%, up to ±20% when far off). Returns 0 only when the client is far
   * *behind*, where a forward jump is the right fix; a backward jump would re-stamp inputs the
   * server already holds, so being far ahead is handled by slowing down.
   */
  dilation(reportedLead: number) {
    const err = this.targetLead() - reportedLead;
    if (err > 20) return 0;
    const limit = Math.abs(err) > 10 ? 0.2 : 0.05;
    return 1 + Math.max(-limit, Math.min(limit, err * 0.01));
  }
}

/**
 * Client side: the clock remote creatures render by. It estimates the server tick from the
 * least-delayed recent snapshot arrivals (the upper envelope is jitter-free), sits `delay`
 * ticks behind it with delay sized to the observed arrival spread, and advances smoothly: it
 * speeds up or slows down by at most 10% and only snaps on a large error. A clock re-derived
 * from each arrival jumps when snapshots bunch up after a stall, and the remote creature pops.
 */
export class RenderClock {
  tick = 0;
  delayTicks = 6;
  private samples: { offset: number; at: number }[] = [];
  private started = false;

  observe(serverTick: number, arrivedMs: number) {
    this.samples.push({ offset: serverTick - (arrivedMs * 60) / 1000, at: arrivedMs });
    while (this.samples.length && arrivedMs - (this.samples[0] as { at: number }).at > 3000) {
      this.samples.shift();
    }
  }

  advance(nowMs: number, dtMs: number, baseDelay: number) {
    if (!this.samples.length) return this.tick;
    const offsets = this.samples.map((x) => x.offset).sort((a, b) => a - b);
    const best = offsets[offsets.length - 1] as number;
    const late = offsets[Math.floor(offsets.length * 0.1)] as number;
    // Cover ~90% of arrivals: the base delay plus how far the slow tail lags the best case.
    this.delayTicks = baseDelay + Math.min(20, Math.ceil(best - late));
    const target = (nowMs * 60) / 1000 + best - this.delayTicks;
    if (!this.started || Math.abs(target - this.tick) > 30) {
      this.tick = target;
      this.started = true;
      return this.tick;
    }
    const rate = 1 + Math.max(-0.1, Math.min(0.1, (target - this.tick) * 0.05));
    this.tick += ((dtMs * 60) / 1000) * rate;
    return this.tick;
  }
}
